import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled, { keyframes } from 'styled-components';

import { QuestionMarkIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Callout,
	Checkbox,
	Label,
	Paragraph,
	Select,
	Subheading,
	Tooltip,
	tokens,
} from '@treams/treams-ui';

import { formatRevealValue } from './formatRevealValue';
import { RevealFieldPreview } from './useConversationRevealUpload';

export const MAX_FIELDS = 15;

const slideIn = keyframes`
	from { opacity: 0; transform: translateY(8px); }
	to { opacity: 1; transform: translateY(0); }
`;

const FieldRow = styled(Box)<{ index: number }>`
	opacity: 0;
	animation: ${slideIn} 0.3s ease-out forwards;
	animation-delay: ${(props) => props.index * 0.05}s;
`;

const FieldGrid = styled(Box)`
	display: grid;
	grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) 140px;
	column-gap: 16px;
	align-items: center;
`;

const QuestionMark = styled(Box)`
	line-height: 0;
	cursor: help;

	svg {
		display: block;
		width: 16px;
		height: 16px;

		path {
			fill: ${tokens.text.secondary};
		}
	}
`;

const FIELD_TYPE_LABEL_KEYS: Record<graphql.RevealFieldType, string> = {
	[graphql.RevealFieldType.Text]: 'conversations.reveal.fieldType.text',
	[graphql.RevealFieldType.Numeric]: 'conversations.reveal.fieldType.numeric',
	[graphql.RevealFieldType.Currency]: 'conversations.reveal.fieldType.currency',
};

interface IProps {
	summary: { detectedFields: string[]; fieldPreviews: RevealFieldPreview[]; matchColumn: string };
	selectedFields: string[];
	fieldTypes: Record<string, graphql.RevealFieldType>;
	onToggleField: (field: string) => void;
	onChangeFieldType: (field: string, type: graphql.RevealFieldType) => void;
}

export function ConversationRevealFieldPreview(props: IProps) {
	const { t, i18n } = useTranslation();

	// Fall back to plain TEXT rows when the summary has no previews (should
	// not happen, but keeps the modal usable).
	const fieldPreviews: RevealFieldPreview[] =
		props.summary.fieldPreviews.length > 0
			? props.summary.fieldPreviews
			: props.summary.detectedFields.map((label) => ({
					label,
					type: graphql.RevealFieldType.Text,
				}));

	const tooManyFields = props.selectedFields.length > MAX_FIELDS;

	const typeOptions = Object.entries(FIELD_TYPE_LABEL_KEYS).map(([value, labelKey]) => ({
		value,
		title: t(labelKey),
	}));

	return (
		<Box>
			{tooManyFields && (
				<Callout color="yellow" mb={3}>
					{t('conversations.reveal.upload.tooManyFields', { max: MAX_FIELDS })}
				</Callout>
			)}

			<Paragraph color="slateBlue" mb={3}>
				{t('conversations.reveal.upload.matchedOnColumn', {
					column: props.summary.matchColumn,
				})}
			</Paragraph>

			<Flex alignItems="center" mb={2} style={{ gap: 8 }}>
				<Subheading>{t('conversations.reveal.upload.selectFields')}</Subheading>
				<Tooltip
					portal
					content={
						<Box>
							<Paragraph mb={2}>{t('conversations.reveal.upload.explanation.manager')}</Paragraph>
							<Paragraph>{t('conversations.reveal.upload.explanation.employee')}</Paragraph>
						</Box>
					}
				>
					<QuestionMark>
						<QuestionMarkIcon />
					</QuestionMark>
				</Tooltip>
			</Flex>
			<FieldGrid mb={2}>
				<Box />
				<Label>{t('conversations.reveal.upload.exampleColumn')}</Label>
				<Label>{t('conversations.reveal.upload.typeColumn')}</Label>
			</FieldGrid>
			{fieldPreviews.map((field, index) => {
				const type = props.fieldTypes[field.label] ?? field.type;

				return (
					<FieldRow key={field.label} index={index} mb={2}>
						<FieldGrid>
							<Checkbox
								label={field.label}
								checked={props.selectedFields.includes(field.label)}
								onClick={() => props.onToggleField(field.label)}
							/>
							<Paragraph
								color="slateBlue"
								style={{
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									whiteSpace: 'nowrap',
								}}
							>
								{field.example
									? formatRevealValue({ value: field.example, type }, i18n.language)
									: '—'}
							</Paragraph>
							<Box>
								<Select
									small
									search={false}
									options={typeOptions}
									selectedValue={type}
									onChange={(value) =>
										value && props.onChangeFieldType(field.label, value as graphql.RevealFieldType)
									}
								/>
							</Box>
						</FieldGrid>
					</FieldRow>
				);
			})}
		</Box>
	);
}
